import { InfoOutlined } from '@mui/icons-material';
import { Avatar, Box, Chip, Stack, Typography } from '@mui/material';
import capitalize from 'lodash/capitalize';

import StaffLoginChip from '@/components/StaffLoginChip';
import { AvatarS3PhotoKey } from '@/components/ui/Avatar/AvatarS3PhotoKey';
import { ExaCareAuth0User } from '@/hooks/useCurrentUser';
import { UserPayload, UserStatus } from '@/hooks/useUserQuery';
import { Role } from '@/hooks/useUserTypesQuery';
import { UserDynamicPermissions } from '@/pages/AdminHub/Staff/hooks/useDynamicPermissionQuery';
import theme from '@/theme';

const rolePriorityMapping: Record<Role, number> = {
  A0: 8, // ExaCare Support
  L0: 7, // Admin
  'L0.1': 6, // Executive Director
  L1: 5, // Manager/ Head Nurse
  L2: 4, // Nurse
  L3: 3, // Medtech
  'L3.1': 2, // Caregiver (medications enabled)
  L4: 1, // Caretaker
  L5: 0 // Support Staff
};

const statusChipColorMapping: Record<
  UserStatus,
  'default' | 'primary' | 'warning' | 'error'
> = {
  active: 'default',
  pending: 'warning',
  deactivated: 'error'
};

type PartiallyRequired<T, K extends keyof T> = Omit<T, K> &
  Required<Pick<T, K>>;

export interface UserModel
  extends UserPayload,
    PartiallyRequired<ExaCareAuth0User, 'organization_id' | 'role'> {}

export class UserModel extends UserPayload {
  private readonly enabledDynamicPermissions: Set<string>;

  constructor(user: UserPayload) {
    super(user);
    this.enabledDynamicPermissions = new Set(
      user?.enabled_dynamic_permissions || []
    );
  }

  public isAdmin = () =>
    this.user_type?.type === 'L0' || this.user_type?.type === 'A0';

  public hasMinimumUserRole = (role: Role): boolean =>
    rolePriorityMapping[this.user_type?.type as Role] >=
    rolePriorityMapping[role];

  public isRoleHigherOrEqualThan = (roleFrom: Role, roleTo: Role): boolean =>
    rolePriorityMapping[roleFrom] >= rolePriorityMapping[roleTo];

  public getFullName = (): string => `${this.first_name} ${this.last_name}`;

  public getInitials = (): string =>
    `${this.first_name?.charAt(0)}${this.last_name?.charAt(0)}`;

  public getPosition = (): string => this.user_type?.title ?? '';

  public getTitle = (): string => this.title ?? '';

  public getCapitalizedStatus = (): string => capitalize(this.status) ?? '';

  private renderAvatar = (): JSX.Element => {
    if (this.s3_photo_url) {
      return (
        <Avatar
          src={this.s3_photo_url}
          sx={{ height: 32, width: 32, fontSize: '1rem' }}
        >
          {this.getInitials()}
        </Avatar>
      );
    }

    return (
      <AvatarS3PhotoKey
        s3PhotoKey={this.s3_photo_key}
        sx={{ height: 32, width: 32, fontSize: '1rem' }}
      >
        {this.getInitials()}
      </AvatarS3PhotoKey>
    );
  };

  public renderStatusCell = (): JSX.Element => {
    const status =
      this.status === UserStatus.DEACTIVATED
        ? 'Deactivated'
        : this.getCapitalizedStatus();
    return (
      <Chip
        label={status}
        color={statusChipColorMapping[this.status]}
        size="small"
      />
    );
  };

  public renderFullNameCell = (currentUser: UserModel): JSX.Element => (
    <Box display="flex" alignItems="center" gap="8px">
      {this.renderAvatar()}
      <Stack spacing={1} sx={{ width: '150px' }}>
        {this.getFullName()}
        <Stack spacing={1} direction="row" alignItems="center">
          {this.auth_method === 'sso' && <Chip label="SSO" size="small" />}
          {!this.last_sign_in_time &&
            currentUser.hasMinimumUserRole('L0.1') && <StaffLoginChip />}
        </Stack>
      </Stack>
    </Box>
  );

  public renderDetailsCell = (showNotAllowedIcon = false): JSX.Element => (
    <Box display="flex" alignItems="center" gap="8px">
      <Box>{this.renderAvatar()}</Box>
      <Box display="column">
        <Box display="flex" alignItems="center" gap="8px">
          {this.getFullName()}
          <Typography variant="body2">{this.getTitle()}</Typography>
          {showNotAllowedIcon && (
            <InfoOutlined
              sx={{
                fontSize: '1rem',
                color: theme.palette.text.secondary,
                marginLeft: 'auto'
              }}
            />
          )}
        </Box>
        <Box display="flex" alignItems="center">
          <Typography variant="body2" color={theme.palette.primary.main}>
            {this.email}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  public isSystemPermissionsEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.WRITE_SYSTEM_PERMISSIONS
    ) || this.isAdmin();

  public isCRMScreenerRulesAndDataPullsPermissionsEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_RULES_AND_DATA_PULLS
    );

  public isCRMScreenerGlobalRulesAndDataPullsPermissionsEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_RULES_AND_DATA_PULLS_GLOBAL
    );

  public isCRMScreenerEditReviewStagePermissionsEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_EDIT_REVIEW_STAGE
    );

  public isCRMScreenerEditAdmissionFlags = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_EDIT_ADMISSION_FLAGS
    );

  public isCRMScreenerEditOrganizationStatesEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_MANAGE_ORGANIZATION_STATES
    );

  public isCRMScreenerManageWaitlistEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_MANAGE_WAITLIST
    );

  public isCRMScreenerSendHospitalChatMessagesEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_SEND_HOSPITAL_CHAT_MESSAGES
    );

  public isCRMScreenerOverrideAnalysisResultsEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_OVERRIDE_ANALYSIS_RESULTS
    );

  public isCRMScreenerDeleteReferralsEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_DELETE_REFERRALS
    );

  public isUserManagementAddEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.USER_MANAGEMENT_ADD
    );

  public isUserManagementDeleteEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.USER_MANAGEMENT_DELETE
    );

  public isCRMScreenerManageSourcesEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_MANAGE_SOURCES
    );

  public isEditUserTypeNotificationPreferencesEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.EDIT_USER_TYPE_NOTIFICATION_PREFERENCES
    );

  public clone = () => {
    const payload = Object.assign({}, this);
    const clonedUserModel = new UserModel(payload);
    return clonedUserModel;
  };

  public isAssessmentsEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.ASSESSMENT_REVIEW_AND_CARE_SUBSCRIPTIONS
    );

  public isDataCenterEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.DATA_CENTER_GENERAL
    );

  public isPhoneAgentEnabled = () =>
    this.enabledDynamicPermissions.has(UserDynamicPermissions.CRM_PHONE_AGENT);

  public isCRMScreenerPalPreAuthDocumentExtractionEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_PAL_PRE_AUTH_DOCUMENT_EXTRACTION
    );

  public isScreenerCustomer = () =>
    this.organization_settings?.is_screener_customer === true;

  public getSearchText = () => {
    return [this.getFullName(), this.email, this.getPosition(), this.getTitle()]
      .join(' ')
      .toLowerCase();
  };
}
